<template>
  <button
    class="kds-button"
    :class="[
      `kds-button--${resolvedVariant}`,
      `kds-button--${size}`,
      { 'kds-button--with-icon': !!icon, 'kds-button--loading': loading }
    ]"
    :type="type"
    :disabled="disabled || loading"
    :aria-disabled="disabled || loading ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    @click="onClick"
  >
    <KdsIcon v-if="icon" :name="icon" :size="size === 'sm' ? 14 : 18" />
    <span class="kds-button__label">
      <slot />
    </span>
    <span v-if="loading" class="kds-button__spinner" aria-hidden="true" />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import KdsIcon from './KdsIcon.vue';
import type {
  KdsButtonSize,
  KdsButtonVariant,
  KdsIconName
} from '@/kds/types';

interface Props {
  variant?: KdsButtonVariant;
  size?: KdsButtonSize;
  icon?: KdsIconName;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  icon: undefined,
  disabled: false,
  loading: false,
  type: 'button'
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const KNOWN: KdsButtonVariant[] = [
  'primary',
  'secondary',
  'ghost',
  'signal',
  'danger'
];

// Fallback safety (edge case: unknown variant → primary).
const resolvedVariant = computed<KdsButtonVariant>(() =>
  KNOWN.includes(props.variant) ? props.variant : 'primary'
);

function onClick(event: MouseEvent) {
  if (props.disabled || props.loading) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  emit('click', event);
}
</script>

<style scoped lang="scss">
.kds-button {
  --kds-btn-radius: var(--r-3);
  --kds-btn-pad-y: 10px;
  --kds-btn-pad-x: 16px;
  --kds-btn-fs: 14px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: var(--bw) solid transparent;
  border-radius: var(--kds-btn-radius);
  padding: var(--kds-btn-pad-y) var(--kds-btn-pad-x);
  font-family: var(--font-ui);
  font-size: var(--kds-btn-fs);
  font-weight: var(--fw-medium);
  line-height: 1.2;
  cursor: pointer;
  transition:
    background-color var(--d-fast) var(--ease-out),
    border-color var(--d-fast) var(--ease-out),
    color var(--d-fast) var(--ease-out),
    box-shadow var(--d-fast) var(--ease-out);
  white-space: nowrap;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &--sm {
    --kds-btn-radius: var(--r-2);
    --kds-btn-pad-y: 6px;
    --kds-btn-pad-x: 10px;
    --kds-btn-fs: 12.5px;
  }

  &--primary {
    background: var(--ink);
    color: var(--paper);
  }
  &--primary:hover:not(:disabled) {
    background: var(--ink-3);
  }
  &--primary:active:not(:disabled) {
    background: var(--ink-2);
  }

  &--secondary {
    background: var(--paper);
    color: var(--ink);
    border-color: var(--mist);
  }
  &--secondary:hover:not(:disabled) {
    background: var(--paper-3);
    border-color: var(--mist-2);
  }

  &--ghost {
    background: transparent;
    color: var(--ink);
  }
  &--ghost:hover:not(:disabled) {
    background: var(--paper-3);
  }

  &--signal {
    background: var(--signal);
    color: var(--ink);
    border-color: var(--signal-press);
  }
  &--signal:hover:not(:disabled) {
    background: var(--signal-hover);
  }

  &--danger {
    background: var(--paper);
    color: var(--danger);
    border-color: var(--mist);
  }
  &--danger:hover:not(:disabled) {
    background: var(--paper-3);
  }

  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__label {
    display: inline-block;
  }

  &--loading {
    position: relative;
  }

  &--loading .kds-button__label,
  &--loading > svg {
    /* Keep dimensions stable — no layout shift while loading. */
    visibility: hidden;
  }

  &__spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 14px;
    height: 14px;
    margin: -7px 0 0 -7px;
    border: 1.5px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: kds-button-spin 720ms linear infinite;
  }
}

@keyframes kds-button-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .kds-button__spinner {
    animation-duration: 0s;
  }
}
</style>
